import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Blocks } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { SHARED_BLOCKS_KEY, useSharedBlocks } from '../lib/useSharedBlocks';
import { ApiError, type SharedBlock } from '../types/api';

function usageText(block: SharedBlock): string {
  const t = block.usage?.templates ?? 0;
  const d = block.usage?.draftCampaigns ?? 0;
  if (t === 0 && d === 0) return 'Not used yet';
  const parts = [];
  if (t > 0) parts.push(`${t} ${t === 1 ? 'template' : 'templates'}`);
  if (d > 0) parts.push(`${d} draft ${d === 1 ? 'campaign' : 'campaigns'}`);
  return `Used by ${parts.join(' and ')}`;
}

function errorText(error: unknown, fallback: string): string | null {
  if (!error) return null;
  if (error instanceof ApiError) {
    if (error.status === 403) return 'You do not have permission to change shared blocks.';
    if (error.body.error === 'shared_block_in_use') {
      return 'This block is still used by a template or draft. Remove it from them first.';
    }
    if (error.body.message) return error.body.message;
  }
  return fallback;
}

/**
 * Settings, Shared blocks (K4). A block saved once and reused in many
 * templates. An edit here reaches every draft and automation mail the next
 * time it is rendered; a campaign that was already approved keeps the HTML
 * that was approved.
 */
export function SharedBlocksPage() {
  const { isEditor } = useAuth();
  const queryClient = useQueryClient();
  const { blocks, isLoading, isError } = useSharedBlocks();
  const [editing, setEditing] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftHtml, setDraftHtml] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: SHARED_BLOCKS_KEY });

  const saveMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch<SharedBlock>(`/shared-blocks/${id}`, {
        method: 'PUT',
        body: { name: draftName, html: draftHtml },
      }),
    onSuccess: (block) => {
      setEditing(null);
      setNotice(`"${block.name}" has been saved.`);
      invalidate();
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (block: SharedBlock) =>
      apiFetch<{ result: string }>(`/shared-blocks/${block.id}`, { method: 'DELETE' }),
    onSuccess: (_res, block) => {
      setNotice(`"${block.name}" has been deleted.`);
      invalidate();
    },
  });

  const rowError =
    errorText(saveMutation.error, 'The block could not be saved.') ??
    errorText(deleteMutation.error, 'The block could not be deleted.');

  return (
    <>
      <PageHeader
        title="Shared blocks"
        badge={<MockBadge />}
        description="Save a block once and use it in many templates. Editing it here updates every draft and automation mail the next time it is rendered. A campaign that is already approved keeps the version that was approved. To create one, open a template and choose Save as shared block on any block."
      />
      {!isEditor ? <Alert>You have read-only access.</Alert> : null}
      {notice ? (
        <div role="status" className="text-success-text text-sm">
          {notice}
        </div>
      ) : null}
      {rowError ? <Alert>{rowError}</Alert> : null}
      {isLoading ? <div className="text-muted">Loading shared blocks…</div> : null}
      {isError ? <Alert>The shared blocks could not be loaded.</Alert> : null}
      {!isLoading && !isError && blocks.length === 0 ? (
        <EmptyState
          icon={<Blocks />}
          title="No shared blocks yet"
          description="Good candidates are a shipping note, a returns promise or a footer. Open a template, expand a block and choose Save as shared block."
        />
      ) : null}
      <ul className="flex flex-col gap-3 list-none p-0 m-0">
        {blocks.map((block) => (
          <li key={block.id}>
            <Card className="p-4">
              {editing === block.id ? (
                <div className="flex flex-col gap-2">
                  <Input
                    aria-label="Block name"
                    maxLength={80}
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                  />
                  <textarea
                    aria-label="Block HTML"
                    className="w-full min-h-[160px] rounded-[9px] border border-zinc-300 px-3 py-2 text-sm font-mono"
                    value={draftHtml}
                    onChange={(e) => setDraftHtml(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      disabled={saveMutation.isPending || draftName.trim() === ''}
                      onClick={() => saveMutation.mutate(block.id)}
                    >
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-wrap">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="font-medium">{block.name}</div>
                    <div className="text-muted text-sm">{usageText(block)}</div>
                  </div>
                  {isEditor ? (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setNotice(null);
                          setEditing(block.id);
                          setDraftName(block.name);
                          setDraftHtml(block.html);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        aria-label={`Delete ${block.name}`}
                        disabled={deleteMutation.isPending}
                        onClick={() => {
                          setNotice(null);
                          deleteMutation.mutate(block);
                        }}
                      >
                        Delete
                      </Button>
                    </>
                  ) : null}
                </div>
              )}
            </Card>
          </li>
        ))}
      </ul>
    </>
  );
}
